import { Outlet } from 'react-router';
import { useEffect, Fragment } from 'react';
import { ArrowRight } from 'lucide-react';
import { useQuery } from '@apollo/client/react';

import { SoftwareEscrowContextProvider, useSoftwareEscrow } from 'src/context/software-escrow';
import { Main } from 'src/components/Layout';
import { EscrowPartyTypeEnum, EscrowStatusEnum } from 'src/graphql/schema';
import { ClientChangesRequestedDocument } from 'src/graphql/actions/suggestChanges/clientChangesRequested.query';
import { softwareEscrowStatusMap as statusMap } from 'src/shared/mappings/softwareEscrowStatusMap';
import { Badge } from '@/components/ui/badge';
import { cn } from 'src/lib/utils';
import { suggestChangesVar, writeResetSuggestChanges } from 'src/graphql/client/cache';
import { withSuggestedChangesApi } from 'src/containers/SoftwareEscrow/RequestChanges';
import { RequestTerminationDialog } from 'src/containers/SoftwareEscrow/Termination';
import { OrganizationLogo } from 'src/components/Organization';
import { usePageMeta } from 'src/hooks';
import {
  getEscrowOnboardingStep,
  setEscrowOnboardingStep,
  resetEscrowOnboardingStep,
} from 'src/utils/escrowOnboardingLocalStorage';
import { IconBadgeCertified, IconBadgeVerified, IconBadgeValidated } from 'src/components/Svg/Images';
import { getActiveCertification, getEscrowStatus } from 'src/utils/escrow';
import { useOnboardingGuard } from 'src/hooks/use-onboarding-guard';
import { IconBeneficiaryH, IconDepositorH } from 'src/components/Svg/Icons';
import { EscrowAlertBadge } from 'src/components/Escrow/AlertBadge';

import { ActionBanner, ActivationBanner, NavigationTabs, ActionDropdown } from '.';

const ActiveRequestBadge = () => {
  const {
    softwareEscrow: { status, escrowTermination },
  } = useSoftwareEscrow();
  const { data: changesRequested } = useQuery(ClientChangesRequestedDocument);

  let variant = null;
  const isTerminationState = status === EscrowStatusEnum.PendingTermination || Boolean(escrowTermination);

  if (changesRequested?.clientChangesRequested.isRequested) variant = 'warning' as const;
  if (isTerminationState) variant = 'alert' as const;
  if (!variant) return null;

  return <EscrowAlertBadge key={variant} variant={variant} className="size-5" iconClassName="size-4.5" />;
};

const StatusBadge = () => {
  const {
    softwareEscrow: { status: escrowStatus, agreement, escrowTermination, escrowInvitation, pendingActivation },
  } = useSoftwareEscrow();

  const status = getEscrowStatus({
    escrowStatus,
    escrowAgreementApproved: Boolean(agreement?.approved),
    escrowTermination,
    escrowInvitationStatus: escrowInvitation?.status,
    pendingActivation,
  });

  const { label, color, bgColor } = statusMap[status as keyof typeof statusMap];

  return <Badge className={cn('whitespace-nowrap', bgColor, color)}>{label}</Badge>;
};

const CertificateBadges = () => {
  const {
    softwareEscrow: { certificationCertified, certificationVerified, activatedAt },
  } = useSoftwareEscrow();

  const activeCertification = getActiveCertification({ certificationCertified, certificationVerified, activatedAt });

  if (!activeCertification) return null;

  return (
    <div className="flex items-center gap-1">
      <IconBadgeValidated className={cn('size-5 opacity-30', activatedAt && 'size-6 opacity-100')} />
      <IconBadgeVerified className={cn('size-5 opacity-30', certificationVerified && 'size-6 opacity-100')} />
      <IconBadgeCertified className={cn('size-5 opacity-30', certificationCertified && 'size-6 opacity-100')} />
    </div>
  );
};

const Parties = () => {
  const {
    softwareEscrow: { depositor, beneficiary, escrowInvitation },
  } = useSoftwareEscrow();

  const parties = [
    {
      partyType: EscrowPartyTypeEnum.Depositor,
      data: depositor,
    },
    {
      partyType: EscrowPartyTypeEnum.Beneficiary,
      data: beneficiary,
    },
  ].map(({ partyType, data }) => ({
    id: data?.id || `new-${partyType}`,
    companyWebsite: data?.companyWebsite,
    companyName: data?.companyName || escrowInvitation?.companyName || '',
    partyType,
    isNew: !data,
  }));

  return (
    <div className="flex flex-row flex-wrap items-center gap-2 sm:flex-nowrap">
      {parties.map(({ id, companyWebsite, companyName, isNew, partyType }, index) => {
        const PartyIcon = partyType === EscrowPartyTypeEnum.Depositor ? IconDepositorH : IconBeneficiaryH;

        return (
          <Fragment key={id}>
            <div className="flex items-center gap-2">
              {isNew ? (
                <div className="size-8 border border-muted bg-muted rounded-lg flex items-center justify-center">
                  <PartyIcon className="size-5 text-gray-400" />
                </div>
              ) : (
                <OrganizationLogo logo={companyWebsite ?? ''} name={companyName} size="md" rounded="lg" />
              )}
              <div className={cn('text-lg md:text-xl text-foreground font-bold', isNew && 'text-gray-400 font-medium')}>
                {companyName}
              </div>
            </div>
            {index < parties.length - 1 && <ArrowRight className="shrink-0 size-4 md:size-5 text-muted-foreground" />}
          </Fragment>
        );
      })}
    </div>
  );
};

const Header = withSuggestedChangesApi(() => {
  return (
    <div className="px-6 pt-4">
      <div className="container mx-auto space-y-5">
        <div className="flex items-start sm:items-center justify-between gap-4">
          <div className="flex flex-col-reverse gap-5 sm:flex-row sm:items-center sm:flex-wrap sm:gap-2">
            <Parties />
            <div className="flex flex-row-reverse justify-end sm:flex-row items-center gap-2">
              <StatusBadge />
              <CertificateBadges />
              <ActiveRequestBadge />
            </div>
          </div>
          <ActionDropdown />
        </div>
        <ActivationBanner />
        <ActionBanner />
        <NavigationTabs />
      </div>
    </div>
  );
});

const Layout = () => {
  const { softwareEscrow } = useSoftwareEscrow();

  const escrowName = [softwareEscrow?.depositor?.companyName, softwareEscrow?.beneficiary?.companyName]
    .filter(Boolean)
    .join(' <> ');

  usePageMeta(escrowName);

  const breadcrumbItems = [
    {
      label: softwareEscrow.app.name ?? 'Untitled Application',
      to: `/applications/${softwareEscrow.app.id}`,
    },
    {
      label: escrowName,
    },
  ];

  const onboardingGuard = useOnboardingGuard({
    escrow: softwareEscrow,
    basePath: '/software-escrows/onboarding',
    getStep: getEscrowOnboardingStep,
    setStep: setEscrowOnboardingStep,
    removeStep: resetEscrowOnboardingStep,
  });
  if (onboardingGuard) return onboardingGuard;

  return (
    <Main breadcrumbItems={breadcrumbItems} headerChildren={<Header />} className="bg-background-secondary">
      <Outlet />
      <RequestTerminationDialog />
    </Main>
  );
};

const SoftwareEscrowLayout = () => {
  // Clean up suggestChangesVar on unmount
  useEffect(() => {
    return () => {
      writeResetSuggestChanges(suggestChangesVar);
    };
  }, []);

  return (
    <SoftwareEscrowContextProvider>
      <Layout />
    </SoftwareEscrowContextProvider>
  );
};

export default SoftwareEscrowLayout;
